import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Locale-aware date formatting via Intl, modeled on politis mobile's
// useDateFormatter. The wire dates are revived as UTC instants (the naive-UTC
// contract — plan.md §3.4/§7 #10), so we pin timeZone: 'UTC' to display exactly
// what the server sent regardless of the viewer's timezone (and to keep the
// numeric output deterministic for tests). The locale follows the active UI
// language, so dates localize with bg/en.
const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
}

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTS,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
}

// An Invalid Date is truthy, so a plain `date ?` guard lets it through to
// Intl.format, which throws "Invalid time value". Treat any non-Date or
// unparseable instant as "no displayable date" — same outcome as null.
function isDisplayable(date: Date | null | undefined): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

export function useDateFormatter() {
  const { i18n } = useTranslation()
  const locale = i18n.language || 'bg'

  const formatDate = useCallback(
    (date: Date | null | undefined) =>
      isDisplayable(date) ? new Intl.DateTimeFormat(locale, DATE_OPTS).format(date) : '—',
    [locale],
  )

  const formatDateTime = useCallback(
    (date: Date | null | undefined) =>
      isDisplayable(date) ? new Intl.DateTimeFormat(locale, DATETIME_OPTS).format(date) : '—',
    [locale],
  )

  return { formatDate, formatDateTime }
}
